import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { 
  createProduct, 
  getBarberProducts, 
  updateProduct, 
  getShippingCost, 
  updateShippingCost,
  getBarberOrders
} from '../../services/api';
import { Product, Order } from '../../types/shop';
import { ExpandMore } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

const StoreManagement: React.FC = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Partial<Product>>({});
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [newShippingCost, setNewShippingCost] = useState('0');
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [salesStats, setSalesStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    averageOrderValue: 0,
  });

  useEffect(() => {
    fetchProducts();
    fetchShippingCost();
    fetchOrders();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getBarberProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchShippingCost = async () => {
    try {
      if (!user || !user.id) {
        console.error('User ID not available');
        return;
      }
      const cost = await getShippingCost(Number(user.id));
      setShippingCost(cost);
      setNewShippingCost(cost.toString());
    } catch (error) {
      console.error('Error fetching shipping cost:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const data = await getBarberOrders();
      setOrders(data);
      
      // Calculate stats
      const totalSales = data.reduce((sum, order) => sum + order.total_amount, 0);
      const totalOrders = data.length;
      const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
      
      setSalesStats({
        totalSales,
        totalOrders,
        averageOrderValue,
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (currentProduct.id) {
        await updateProduct(currentProduct.id, currentProduct);
      } else {
        await createProduct(currentProduct);
      }
      await fetchProducts();
      setOpen(false);
      setCurrentProduct({});
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveShippingCost = async () => {
    try {
      if (!user || !user.id) {
        console.error('User ID not available');
        return;
      }
      
      const cost = parseFloat(newShippingCost);
      if (isNaN(cost) || cost < 0) {
        // Handle invalid input
        return;
      }
      
      await updateShippingCost(Number(user.id), cost);
      setShippingCost(cost);
      setIsEditingShipping(false);
    } catch (error) {
      console.error('Error updating shipping cost:', error);
    }
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Shipping Cost</Typography>
              
              {isEditingShipping ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Shipping Cost"
                    type="number"
                    value={newShippingCost}
                    onChange={(e) => setNewShippingCost(e.target.value)}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSaveShippingCost}
                    size="small"
                  >
                    Save
                  </Button>
                  <Button 
                    onClick={() => {
                      setIsEditingShipping(false);
                      setNewShippingCost(shippingCost.toString());
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5">${shippingCost.toFixed(2)}</Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => setIsEditingShipping(true)}
                    size="small"
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sales Dashboard Section */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="sales-dashboard-content"
          id="sales-dashboard-header"
        >
          <Typography variant="h6">Sales Dashboard</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary">Total Sales</Typography>
                  <Typography variant="h4">${salesStats.totalSales.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary">Total Orders</Typography>
                  <Typography variant="h4">{salesStats.totalOrders}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary">Average Order Value</Typography>
                  <Typography variant="h4">${salesStats.averageOrderValue.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>Recent Orders</Typography>
          
          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Typography>No orders found.</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>#{order.id}</TableCell>
                      <TableCell>{format(new Date(order.created_at), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{order.client_name || `Client #${order.client_id}`}</TableCell>
                      <TableCell>
                        {order.items.map((item) => (
                          <div key={item.id}>
                            {item.quantity}x {item.product.name}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell align="right">${order.total_amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: 
                              order.status === 'completed' ? 'success.light' : 
                              order.status === 'pending' ? 'warning.light' : 
                              order.status === 'cancelled' ? 'error.light' : 'info.light',
                            color: 
                              order.status === 'completed' ? 'success.dark' : 
                              order.status === 'pending' ? 'warning.dark' : 
                              order.status === 'cancelled' ? 'error.dark' : 'info.dark',
                          }}
                        >
                          {order.status}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Store Management</Typography>
          <Button 
            variant="contained" 
            onClick={() => {
              setCurrentProduct({});
              setOpen(true);
            }}
          >
            Add Product
          </Button>
        </Grid>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.description}</TableCell>
                  <TableCell>${product.price}</TableCell>
                  <TableCell>{product.stock}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        setCurrentProduct(product);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          {currentProduct.id ? 'Edit Product' : 'Add Product'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={currentProduct.name || ''}
            onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={currentProduct.description || ''}
            onChange={(e) => setCurrentProduct({ ...currentProduct, description: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={currentProduct.price || ''}
            onChange={(e) => setCurrentProduct({ ...currentProduct, price: parseFloat(e.target.value) })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Stock"
            type="number"
            value={currentProduct.stock || ''}
            onChange={(e) => setCurrentProduct({ ...currentProduct, stock: parseInt(e.target.value) })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Image URL"
            value={currentProduct.image_url || ''}
            onChange={(e) => setCurrentProduct({ ...currentProduct, image_url: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StoreManagement; 